<template>
  <a-modal
    :title="title"
    :width="860"
    :visible="visible"
    :confirmLoading="confirmLoading"
    @ok="handleOk"
    @cancel="handleCancel"
    cancelText="取消"
    okText="保存配置"
    :wrapClassName="modalWrapClass"
    class="cycle-config-modal"
  >
    <a-spin :spinning="confirmLoading">
      <a-form :form="form" layout="vertical" class="cycle-form-vertical">
        <!-- 第一行：运维项目 + 运维工作类型 -->
        <a-row :gutter="20">
          <a-col :span="12">
            <a-form-item label="运维项目名称" class="custom-form-item">
              <a-input
                placeholder="例如：月度站点常规巡检、季度水质比对分析"
                v-decorator="['name', { rules: [{ required: true, message: '请输入运维项目名称' }] }]"
              />
              <div class="field-hint-text">规范清晰的业务任务名称，便于日历看板查看</div>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="运维工作类型" class="custom-form-item">
              <a-select
                placeholder="请选择运维工作类型"
                v-decorator="['businessType', { rules: [{ required: true, message: '请选择运维工作类型' }] }]"
                :disabled="isEdit"
                @change="handleBusinessTypeChange"
              >
                <a-select-option v-for="item in businessList" :key="item.businessType" :value="item.businessType">
                  {{ item.name }}
                </a-select-option>
              </a-select>
              <div class="field-hint-text">对应系统注册的业务类型与台账完成基准</div>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- 第二行：运维对象 + 负责角色 (支持真实人员穿透) -->
        <a-row :gutter="20">
          <a-col :span="12">
            <a-form-item label="运维对象 / 适用范围" class="custom-form-item">
              <a-select
                :placeholder="isOnlyAllScope ? '整站通用（无需选择）' : '请选择运维对象'"
                v-decorator="['scopeKey', { rules: [{ required: !isOnlyAllScope, message: '请选择运维对象' }] }]"
                :disabled="isEdit || isOnlyAllScope"
                @change="handleScopeChange"
              >
                <a-select-option v-for="item in scopeList" :key="item.key" :value="item.key">
                  {{ item.label }}
                </a-select-option>
              </a-select>
              <div class="field-hint-text">
                {{ isOnlyAllScope ? '该业务为整站统一运维，无需细分具体模板或设备' : '任务针对的具体设备或范围（如：整站通用、某巡检模板）' }}
              </div>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="负责角色 (执行人)" class="custom-form-item">
              <a-select
                mode="multiple"
                placeholder="请选择负责角色（支持多选）"
                style="width: 100%"
                v-decorator="['roleCodes', { rules: [{ required: true, message: '请选择负责角色' }] }]"
                @change="handleFieldChange"
                :dropdownMatchSelectWidth="false"
              >
                <a-select-option v-for="role in roleOptions" :key="role.value" :value="role.value">
                  <span :class="{ 'role-empty-option': role.members.length === 0 }">
                    {{ role.displayText }}
                  </span>
                </a-select-option>
              </a-select>
              <div v-if="hasEmptyRoleSelected" class="role-warning-highlight">
                <a-icon type="exclamation-circle" style="margin-right: 4px;" />
                部分所选角色在当前站点暂无成员，任务生成后将处于待分配状态
              </div>
              <div v-else class="field-hint-text">任务产生后将自动指派并推送给该站点的对应角色成员</div>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- 第三行：执行周期频次 + 规则启用状态 (对称整洁排版) -->
        <a-row :gutter="20">
          <a-col :span="12">
            <a-form-item label="执行周期频次" class="custom-form-item">
              <!-- 快捷频次单一下拉选择器 -->
              <a-select
                v-model="frequencyMode"
                @change="handleFrequencyModeChange"
                placeholder="请选择执行频次"
                style="width: 100%"
              >
                <a-select-option value="MONTH_1">每月 1 次（自然月） · 常用推荐</a-select-option>
                <a-select-option value="WEEK_1">每周 1 次</a-select-option>
                <a-select-option value="DAY_15">每半月 1 次（15 天）</a-select-option>
                <a-select-option value="DAY_1">每天 1 次</a-select-option>
                <a-select-option value="MONTH_3">每季度 1 次（3 个月）</a-select-option>
                <a-select-option value="MONTH_6">每半年 1 次（6 个月）</a-select-option>
                <a-select-option value="MONTH_12">每年 1 次（12 个月）</a-select-option>
                <a-select-option value="CUSTOM">⚙️ 自定义间隔周期...</a-select-option>
              </a-select>

              <!-- 隐藏的表单项用于绑定 periodValue 和 periodUnit -->
              <div style="display: none;">
                <a-input-number v-decorator="['periodValue', { initialValue: 1 }]" />
                <a-input v-decorator="['periodUnit', { initialValue: 'MONTH' }]" />
              </div>

              <!-- 当选择自定义周期时平滑展开输入 -->
              <div v-if="frequencyMode === 'CUSTOM'" class="custom-cycle-box">
                <span class="custom-cycle-label">每隔</span>
                <a-input-number
                  :min="1"
                  :max="3650"
                  v-model="customValue"
                  @change="handleCustomValueChange"
                  style="width: 90px; margin: 0 6px;"
                />
                <a-select
                  v-model="customUnit"
                  @change="handleCustomUnitChange"
                  style="width: 100px; margin-right: 6px;"
                >
                  <a-select-option value="DAY">天</a-select-option>
                  <a-select-option value="WEEK">周</a-select-option>
                  <a-select-option value="MONTH">自然月</a-select-option>
                </a-select>
                <span class="custom-cycle-label">执行一次</span>
              </div>

              <!-- 频次说明反馈 -->
              <div class="field-hint-text">
                📌 当前设定：{{ getFrequencySummaryText() }}
              </div>
            </a-form-item>
          </a-col>

          <a-col :span="12" v-if="!isEdit">
            <a-form-item label="首次到期时间" class="custom-form-item">
              <a-date-picker v-decorator="['firstDueAt']" show-time format="YYYY-MM-DD HH:mm:ss" @change="triggerPreviewDebounce()" />
              <div class="field-hint-text">无有效记录或历史已逾期时必填；有有效记录时可留空自动计算。</div>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="规则启用状态" class="custom-form-item">
              <div class="enable-switch-row">
                <a-switch
                  checked-children="启用"
                  un-checked-children="停用"
                  v-decorator="['enabled', { valuePropName: 'checked', initialValue: true }]"
                />
                <span class="enable-title-text">开启后系统将自动按周期生成待办任务并在日历中呈现</span>
              </div>
              <div class="field-hint-text">
                💡 系统将自动对接台账记录推算下次执行节点；无台账时使用明确填写的首次到期时间
              </div>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- 第四行：全宽实时规划速览卡片 (深色科技风) -->
        <div class="plan-preview-panel">
          <div class="panel-header">
            <div class="panel-header-left">
              <a-icon type="schedule" class="header-icon" />
              <span class="header-text">执行规划速览 (实时推算)</span>
              <span v-if="previewLoading" class="header-loading-tag">推算中...</span>
            </div>
            <div class="panel-header-right">
              <span class="auto-calc-badge">
                <a-icon type="thunderbolt" style="margin-right: 3px;" />智能自动推算
              </span>
            </div>
          </div>

          <!-- 警告提示 -->
          <div v-if="previewWarnings && previewWarnings.length > 0" class="panel-warnings">
            <div v-for="(w, idx) in previewWarnings" :key="idx" class="warning-row">
              <a-icon type="warning" style="color: #faad14; margin-right: 6px;" />
              <span>{{ w }}</span>
            </div>
          </div>

          <!-- 推算结果 -->
          <div v-if="previewResult" class="panel-body-grid">
            <div class="grid-item">
              <span class="item-label">下次执行节点：</span>
              <span class="item-value highlight-due">{{ previewResult.dueAt || '-' }}</span>
            </div>
            <div class="grid-item">
              <span class="item-label">指派执行人员：</span>
              <span class="item-value">
                <template v-if="previewResult.memberNames && previewResult.memberNames.length > 0">
                  <a-tag v-for="name in previewResult.memberNames" :key="name" color="blue" class="user-name-tag">
                    {{ name }}
                  </a-tag>
                </template>
                <span v-else class="empty-user-tip">暂无匹配成员 (将待指派)</span>
              </span>
            </div>
            <div class="grid-item full-width">
              <span class="item-label">历史台账基准：</span>
              <span class="item-value sub-text">
                {{ previewResult.latestCompletedAt ? `最近台账于 ${previewResult.latestCompletedAt} 完成（已按周期自动推算下次到期）` : '暂无有效完成记录，使用首次到期时间；只修改名称和角色不延期' }}
              </span>
            </div>
            <div class="grid-item full-width natural-box">
              <a-icon type="info-circle" style="color: #40a9ff; margin-right: 6px;" />
              <span>{{ generateNaturalDesc() }}</span>
            </div>
          </div>

          <!-- 空提示 -->
          <div v-else class="panel-empty-text">
            <a-icon type="bulb" style="color: #faad14; margin-right: 6px;" />
            <span>请完善上方【运维工作类型】与【运维对象】，系统将实时推算下次执行节点与责任人员</span>
          </div>
        </div>
      </a-form>
    </a-spin>
  </a-modal>
</template>

<script>
import { getAction, postAction, putAction } from '@/api/manage'

export default {
  name: 'MaintenanceCycleModal',
  data() {
    return {
      title: '操作',
      visible: false,
      confirmLoading: false,
      previewLoading: false,
      isEdit: false,
      siteId: null,
      model: {},
      form: this.$form.createForm(this),
      businessList: [],
      scopeList: [],
      roleOptions: [],
      previewResult: null,
      previewWarnings: [],
      previewTimer: null,
      previewSequence: 0,
      // 执行周期状态
      frequencyMode: 'MONTH_1',
      customValue: 1,
      customUnit: 'MONTH'
    }
  },
  computed: {
    modalWrapClass() {
      return this.$store.state.bgLight ? 'lightColor' : 'darkColor'
    },
    hasEmptyRoleSelected() {
      const selectedRoles = this.form.getFieldValue('roleCodes') || []
      if (!selectedRoles.length) return false
      return selectedRoles.some(val => {
        const found = this.roleOptions.find(r => r.value === String(val))
        return found && found.members.length === 0
      })
    },
    isOnlyAllScope() {
      return this.scopeList.length === 0 || (this.scopeList.length === 1 && this.scopeList[0].key === '*')
    }
  },
  methods: {
    add(siteId) {
      this.isEdit = false
      this.siteId = siteId
      this.title = '新建运维周期规则'
      this.model = { siteId, enabled: true, periodValue: 1, periodUnit: 'MONTH' }
      this.visible = true
      this.previewResult = null
      this.previewWarnings = []
      this.frequencyMode = 'MONTH_1'
      this.customValue = 1
      this.customUnit = 'MONTH'
      this.scopeList = []

      this.initData()
      this.$nextTick(() => {
        this.form.resetFields()
        const defaultBiz = (this.businessList && this.businessList.length > 0) ? this.businessList[0].businessType : undefined
        this.form.setFieldsValue({
          periodValue: 1,
          periodUnit: 'MONTH',
          enabled: true,
          businessType: defaultBiz
        })
        if (defaultBiz) {
          this.loadScopes(defaultBiz)
        }
      })
    },
    edit(record) {
      this.isEdit = true
      this.siteId = record.siteId
      this.title = '编辑运维周期规则'
      this.model = Object.assign({}, record)
      this.visible = true
      this.previewResult = null
      this.previewWarnings = []

      // 匹配频次模式
      this.resolveFrequencyMode(record.periodValue, record.periodUnit)

      this.initData()
      this.loadScopes(record.businessType)

      let initialRoles = []
      if (Array.isArray(record.roleCodeList) && record.roleCodeList.length > 0) {
        initialRoles = record.roleCodeList.map(String)
      } else if (typeof record.roleCodes === 'string') {
        try {
          const parsed = JSON.parse(record.roleCodes)
          initialRoles = Array.isArray(parsed) ? parsed.map(String) : [String(record.roleCodes)]
        } catch (e) {
          initialRoles = record.roleCodes.split(',').filter(Boolean).map(String)
        }
      }

      this.$nextTick(() => {
        this.form.setFieldsValue({
          name: record.name,
          businessType: record.businessType,
          scopeKey: record.scopeKey,
          periodValue: record.periodValue,
          periodUnit: record.periodUnit,
          roleCodes: initialRoles,
          enabled: record.enabled === 1 || record.enabled === true
        })
        this.triggerPreviewDebounce(80)
      })
    },
    initData() {
      this.loadRegisteredBusinesses()
      this.loadSiteRolesAndMembers()
    },
    loadRegisteredBusinesses() {
      getAction('/om/maintenance/businesses').then(res => {
        if (res.success && res.result) {
          this.businessList = res.result
          if (!this.isEdit) {
            const currentBiz = this.form.getFieldValue('businessType')
            if (!currentBiz && this.businessList.length > 0) {
              const defaultBiz = this.businessList[0].businessType
              this.form.setFieldsValue({ businessType: defaultBiz })
              this.loadScopes(defaultBiz)
            }
          }
        }
      })
    },
    // 直接调用后端聚合好的站点角色与人员接口，100% 准确获取真实姓名
    loadSiteRolesAndMembers() {
      if (!this.siteId) return
      getAction(`/om/maintenance/sites/${this.siteId}/role-members`).then(res => {
        if (res.success && res.result) {
          this.roleOptions = res.result.map(item => {
            const members = item.memberNames || []
            return {
              value: String(item.roleCode),
              label: item.roleName,
              members: members,
              displayText: members.length > 0
                ? `${item.roleName}（当前成员：${members.join('、')}）`
                : `${item.roleName}（当前站点暂无成员 ⚠️）`
            }
          })
        }
      }).catch(err => {
        console.error('加载站点角色与成员失败:', err)
      })
    },
    handleBusinessTypeChange(businessType) {
      this.form.setFieldsValue({ scopeKey: undefined })
      this.loadScopes(businessType)
    },
    loadScopes(businessType) {
      if (!businessType || !this.siteId) return
      getAction(`/om/maintenance/businesses/${businessType}/scopes?siteId=${this.siteId}`).then(res => {
        if (res.success && res.result) {
          this.scopeList = res.result
          const isOnlyAll = this.scopeList.length === 0 || (this.scopeList.length === 1 && this.scopeList[0].key === '*')
          if (isOnlyAll) {
            this.form.setFieldsValue({ scopeKey: '*' })
          } else if (!this.isEdit && this.scopeList.length > 0) {
            const currentScope = this.form.getFieldValue('scopeKey')
            if (!currentScope || !this.scopeList.some(s => s.key === currentScope)) {
              this.form.setFieldsValue({ scopeKey: this.scopeList[0].key })
            }
          }
          this.$nextTick(() => {
            this.triggerPreviewDebounce(50)
          })
        }
      })
    },
    handleScopeChange() {
      this.$nextTick(() => {
        this.triggerPreviewDebounce(50)
      })
    },
    // 频次选择器变更
    handleFrequencyModeChange(val) {
      if (val === 'CUSTOM') {
        this.form.setFieldsValue({
          periodValue: this.customValue,
          periodUnit: this.customUnit
        })
      } else {
        const parts = val.split('_')
        const unit = parts[0]
        const value = parseInt(parts[1], 10)
        this.form.setFieldsValue({
          periodValue: value,
          periodUnit: unit
        })
      }
      this.triggerPreviewDebounce(50)
    },
    handleCustomValueChange(val) {
      this.customValue = val || 1
      this.form.setFieldsValue({ periodValue: this.customValue })
      this.triggerPreviewDebounce(50)
    },
    handleCustomUnitChange(val) {
      this.customUnit = val
      this.form.setFieldsValue({ periodUnit: this.customUnit })
      this.triggerPreviewDebounce(50)
    },
    resolveFrequencyMode(val, unit) {
      const mode = `${unit}_${val}`
      const standardModes = ['MONTH_1', 'WEEK_1', 'DAY_15', 'DAY_1', 'MONTH_3', 'MONTH_6', 'MONTH_12']
      if (standardModes.includes(mode)) {
        this.frequencyMode = mode
      } else {
        this.frequencyMode = 'CUSTOM'
        this.customValue = val || 1
        this.customUnit = unit || 'MONTH'
      }
    },
    getFrequencySummaryText() {
      const val = this.form.getFieldValue('periodValue') || 1
      const unit = this.form.getFieldValue('periodUnit') || 'MONTH'
      const unitMap = { DAY: '天', WEEK: '周', MONTH: '自然月' }
      return `每 ${val} ${unitMap[unit] || unit} 执行一次`
    },
    handleFieldChange() {
      this.triggerPreviewDebounce(80)
    },
    triggerPreviewDebounce(delay = 200) {
      if (this.previewTimer) clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(() => {
        this.handlePreview()
      }, delay)
    },
    handlePreview() {
      this.$nextTick(() => {
        const values = this.form.getFieldsValue(['name', 'businessType', 'scopeKey', 'periodValue', 'periodUnit', 'roleCodes', 'firstDueAt', 'enabled'])
        if (!values.businessType || !values.scopeKey) {
          return
        }
        const sequence = ++this.previewSequence
        this.previewLoading = true
        const roleCodesArray = Array.isArray(values.roleCodes) ? values.roleCodes : []
        const postData = Object.assign({}, this.model, values, {
          siteId: this.siteId,
          roleCodes: JSON.stringify(roleCodesArray),
          roleCodeList: roleCodesArray,
          firstDueAt: values.firstDueAt ? values.firstDueAt.format('YYYY-MM-DD HH:mm:ss') : null
        })

        postAction('/om/maintenance/cycles/preview', postData).then(res => {
          if (sequence !== this.previewSequence || !this.visible) return
          if (res.success && res.result) {
            this.previewResult = res.result
            this.previewWarnings = res.result.warnings || []
          } else {
            this.previewResult = null
            if (res.message) {
              this.previewWarnings = [res.message]
            }
          }
        }).catch(err => {
          console.error('推算失败:', err)
        }).finally(() => {
          if (sequence === this.previewSequence) this.previewLoading = false
        })
      })
    },
    generateNaturalDesc() {
      if (!this.previewResult) return ''
      const values = this.form.getFieldsValue()
      const businessName = (this.businessList.find(b => b.businessType === values.businessType) || {}).name || '指定运维'
      const scopeLabel = (this.scopeList.find(s => s.key === values.scopeKey) || {}).label || values.scopeKey || '指定对象'
      const unitText = values.periodUnit === 'DAY' ? '天' : (values.periodUnit === 'WEEK' ? '周' : '个月')
      const periodDesc = `每 ${values.periodValue || 1} ${unitText}`
      const memberDesc = (this.previewResult.memberNames && this.previewResult.memberNames.length > 0)
        ? `指派给【${this.previewResult.memberNames.join('、')}】负责`
        : '暂无分配人员'

      return `系统将针对【${scopeLabel}】以“${periodDesc}”的频次自动规划【${businessName}】任务，预计下次执行时间为 ${this.previewResult.dueAt || '-'}，并${memberDesc}。`
    },
    handleOk() {
      this.form.validateFields((err, values) => {
        if (!err) {
          this.confirmLoading = true
          const roleCodesArray = Array.isArray(values.roleCodes) ? values.roleCodes : []
          const postData = Object.assign({}, this.model, values, {
            siteId: this.siteId,
            enabled: !!values.enabled,
            firstDueAt: values.firstDueAt ? values.firstDueAt.format('YYYY-MM-DD HH:mm:ss') : null,
            roleCodes: JSON.stringify(roleCodesArray),
            roleCodeList: roleCodesArray
          })

          const requestAction = postAction('/om/maintenance/cycles/preview', postData).then(preview => {
            if (!preview.success || !preview.result) throw new Error(preview.message || '预览失败')
            this.previewResult = preview.result
            return new Promise((resolve, reject) => {
              this.$confirm({
                title: '确认运维周期配置',
                content: `到期时间：${preview.result.oldDueAt || '首次配置'} → ${preview.result.dueAt}。${(preview.result.warnings || []).join('；')}`,
                onOk: () => resolve(this.isEdit
                  ? putAction(`/om/maintenance/cycles/${this.model.id}`, postData)
                  : postAction('/om/maintenance/cycles', postData)),
                onCancel: () => reject(new Error('已取消保存'))
              })
            })
          })

          requestAction.then(res => {
            if (res.success) {
              this.$message.success(this.isEdit ? '运维周期配置修改成功' : '新增运维周期配置成功')
              this.visible = false
              this.$emit('ok')
            } else {
              this.$message.error(res.message || '操作失败')
            }
          }).catch(error => {
            this.$message.warning(error.message || '网络请求异常')
          }).finally(() => {
            this.confirmLoading = false
          })
        }
      })
    },
    handleCancel() {
      this.previewSequence++
      if (this.previewTimer) clearTimeout(this.previewTimer)
      this.visible = false
    }
  }
}
</script>

<style lang="less" scoped>
/* 表单垂直紧凑布局 */
.cycle-form-vertical {
  .custom-form-item {
    margin-bottom: 12px;

    /deep/ .ant-form-item-label {
      padding-bottom: 3px;
      line-height: 1.3;
      label {
        font-size: 13px;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.95);
      }
    }
  }
}

/* 提示文本高对比度清晰化 */
.field-hint-text {
  font-size: 12px;
  line-height: 1.4;
  margin-top: 4px;
  color: #a6c8ff; /* 在深蓝底色上清晰可见的高亮浅天蓝 */
}

.role-warning-highlight {
  font-size: 12px;
  line-height: 1.4;
  margin-top: 4px;
  color: #ffc53d;
}

.role-empty-option {
  color: #ffc53d;
}

/* 启用状态行 */
.enable-switch-row {
  display: flex;
  align-items: center;
  height: 32px;
  .enable-title-text {
    margin-left: 10px;
    font-size: 13px;
    color: #ffffff;
  }
}

/* 自定义周期展开栏 */
.custom-cycle-box {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px dashed rgba(24, 144, 255, 0.35);
  border-radius: 4px;

  .custom-cycle-label {
    font-size: 13px;
    color: #e6f7ff;
  }
}

/* 底部全宽规划面板（适配深色科技风） */
.plan-preview-panel {
  background: rgba(24, 144, 255, 0.08);
  border: 1px solid rgba(24, 144, 255, 0.25);
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 6px;

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(24, 144, 255, 0.15);
    padding-bottom: 8px;
    margin-bottom: 10px;

    .panel-header-left {
      display: flex;
      align-items: center;
      .header-icon {
        color: #40a9ff;
        font-size: 16px;
        margin-right: 6px;
      }
      .header-text {
        font-size: 14px;
        font-weight: 600;
        color: #69c0ff;
      }
      .header-loading-tag {
        margin-left: 8px;
        font-size: 12px;
        color: #faad14;
      }
    }

    .panel-header-right {
      .auto-calc-badge {
        font-size: 12px;
        color: #52c41a;
        background: rgba(82, 196, 26, 0.15);
        border: 1px solid rgba(82, 196, 26, 0.35);
        padding: 2px 8px;
        border-radius: 12px;
      }
    }
  }

  .panel-warnings {
    margin-bottom: 8px;
    .warning-row {
      font-size: 12px;
      color: #faad14;
      line-height: 1.5;
    }
  }

  .panel-body-grid {
    display: flex;
    flex-wrap: wrap;
    row-gap: 8px;
    column-gap: 20px;
    font-size: 13px;

    .grid-item {
      display: flex;
      align-items: center;
      width: calc(50% - 10px);

      &.full-width {
        width: 100%;
      }

      .item-label {
        color: #a6c8ff;
        flex-shrink: 0;
      }

      .item-value {
        color: #ffffff;
        font-weight: 500;

        &.highlight-due {
          color: #40a9ff;
          font-weight: 600;
        }

        &.sub-text {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.75);
          font-weight: normal;
        }

        .user-name-tag {
          margin-right: 4px;
        }

        .empty-user-tip {
          color: #faad14;
        }
      }

      &.natural-box {
        background: rgba(24, 144, 255, 0.12);
        border-radius: 4px;
        padding: 6px 10px;
        font-size: 12px;
        color: #e6f7ff;
        line-height: 1.5;
      }
    }
  }

  .panel-empty-text {
    text-align: center;
    padding: 10px 0;
    font-size: 13px;
    color: #a6c8ff;
  }
}

/* 浅色主题适配 */
.lightColor {
  .cycle-form-vertical .custom-form-item /deep/ .ant-form-item-label label {
    color: rgba(0, 0, 0, 0.85);
  }
  .enable-switch-row .enable-title-text {
    color: rgba(0, 0, 0, 0.85);
  }
  .field-hint-text {
    color: #8c8c8c;
  }
  .custom-cycle-box {
    background: #f5f5f5;
    border-color: #d9d9d9;
    .custom-cycle-label {
      color: #595959;
    }
  }
  .plan-preview-panel {
    background: #f0f7ff;
    border-color: #adc6ff;
    .panel-header .panel-header-left .header-text {
      color: #1d39c4;
    }
    .panel-body-grid .grid-item .item-label {
      color: #8c8c8c;
    }
    .panel-body-grid .grid-item .item-value {
      color: #262626;
      &.sub-text {
        color: #8c8c8c;
      }
    }
    .panel-body-grid .grid-item.natural-box {
      background: #e6f7ff;
      color: #1d39c4;
    }
    .panel-empty-text {
      color: #8c8c8c;
    }
  }
}
</style>
